import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { submitApplication } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const INITIAL = {
    full_name: "",
    email: "",
    city: "",
    twitter: "",
    linkedin: "",
    project_url: "",
    organised_before: false,
    organised_details: "",
    who_youd_bring: "",
    why_you: "",
    extra: "",
};

export default function ApplicationForm() {
    const wrap = useRef(null);
    const [form, setForm] = useState(INITIAL);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Prefill "based" field from `?city=` query param (set when linking here
    // from a City Chapters card or a marketing URL).
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const c = params.get("city");
            if (c) {
                setForm((f) => ({ ...f, city: c }));
            }
        } catch {
            /* noop */
        }
    }, []);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".form-reveal").forEach((el) => {
                gsap.fromTo(
                    el,
                    { y: 30, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 0.8,
                        ease: "power3.out",
                        scrollTrigger: { trigger: el, start: "top 92%" },
                    },
                );
            });
        }, wrap);
        return () => ctx.revert();
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.full_name.trim()) return "Please add your full name.";
        if (!/^\S+@\S+\.\S+$/.test(form.email))
            return "Please add a valid email.";
        if (!form.linkedin.trim())
            return "Please add your LinkedIn profile URL.";
        if (!/^https?:\/\/\S+/i.test(form.linkedin.trim()))
            return "LinkedIn must be a full URL starting with http(s)://";
        if (!form.city.trim())
            return "Please tell us where you're based.";
        if (!form.project_url.trim())
            return "Share a link to something you've built.";
        if (!form.who_youd_bring.trim())
            return "Tell us who you'd bring into the room.";
        if (!form.why_you.trim()) return "Tell us why you, why this city.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast(err);
            return;
        }
        setLoading(true);
        try {
            const data = await submitApplication(form);
            setResult(data);
            window.scrollTo({
                top: wrap.current.offsetTop - 80,
                behavior: "smooth",
            });
        } catch (e2) {
            const msg =
                e2?.response?.data?.detail ||
                "Couldn't submit right now. Try again in a minute.";
            toast(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm(INITIAL);
        setResult(null);
    };

    if (result) {
        return (
            <section
                id="apply"
                data-testid="application-success"
                ref={wrap}
                className="relative py-24 md:py-36 border-t border-line bg-[#0f0f0f] text-ink"
            >
                <div className="px-5 md:px-10 lg:px-14 max-w-[1320px] mx-auto">
                    <div className="grid md:grid-cols-12 gap-y-10 md:gap-x-10">
                        <div className="md:col-span-7">
                            <div className="flex items-center gap-4 mb-6">
                                <span className="w-8 h-px bg-white/40" />
                                <span
                                    className="uppercase"
                                    style={{
                                        fontFamily:
                                            '"Figtree", "Inter", system-ui, sans-serif',
                                        fontWeight: 700,
                                        fontSize: "0.72rem",
                                        letterSpacing: "0.25em",
                                        color: "#646464",
                                    }}
                                >
                                    08 / Application received
                                </span>
                            </div>
                            <h2
                                className="uppercase"
                                style={{
                                    fontFamily:
                                        '"FT Activica Normal", "FT Activica Strong", sans-serif',
                                    fontWeight: 400,
                                    fontSize: "clamp(2.4rem, 6vw, 5.6rem)",
                                    letterSpacing: "0.04em",
                                    lineHeight: 1.05,
                                    color: "#FFFCE1",
                                }}
                            >
                                Your Application
                                <br />
                                <span
                                    style={{
                                        background:
                                            "linear-gradient(177.93deg, #07E6F6 40.24%, #003C9D 127.8%)",
                                        WebkitBackgroundClip: "text",
                                        backgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        color: "transparent",
                                    }}
                                >
                                    Is In.
                                </span>
                            </h2>
                            <p
                                className="mt-8 max-w-lg"
                                style={{
                                    fontFamily:
                                        '"Figtree", "Inter", system-ui, sans-serif',
                                    fontWeight: 700,
                                    fontSize: "clamp(15px, 1.1vw, 18px)",
                                    lineHeight: 1.5,
                                    color: "#9d9d96",
                                }}
                            >
                                We read every application. Save your reference
                                ID so you can check status anytime.
                            </p>
                        </div>
                        <div className="md:col-span-5">
                            <div className="border border-white/15 p-8 md:p-12">
                                <div className="vc-section-label text-ink-soft/80 mb-4">
                                    Reference ID
                                </div>
                                <div
                                    data-testid="application-reference-id"
                                    className="font-display font-black tracking-tightest leading-none text-ink break-all"
                                    style={{
                                        fontSize: "clamp(2.4rem, 5vw, 4.2rem)",
                                    }}
                                >
                                    {result.reference_id}
                                </div>
                                <div className="mt-8 flex flex-col gap-4">
                                    <Link
                                        to={`/check/${result.reference_id}`}
                                        data-testid="success-check-status"
                                        className="vc-btn vc-btn-accent vc-btn-lg"
                                    >
                                        Check status →
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        data-testid="success-submit-another"
                                        className="vc-section-label text-ink-soft hover:text-gradient transition-colors text-left"
                                    >
                                        Submit another application →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="apply"
            data-testid="application-section"
            ref={wrap}
            className="relative w-full"
            style={{ background: "#0E100F" }}
        >
            <div className="relative">
                {/* LEFT — hero image, FIXED to viewport beneath the 80px
                    header. We use position:fixed instead of sticky so it
                    works alongside Lenis smooth-scroll (which would
                    otherwise break native sticky pinning). */}
                <div
                    data-testid="apply-left"
                    className="hidden lg:block"
                    style={{
                        position: "fixed",
                        top: 80,
                        left: 0,
                        width: "50%",
                        height: "calc(100vh - 80px)",
                        zIndex: 1,
                    }}
                >
                    <img
                        src="/assets/form-image.jpg"
                        alt="Vibecity"
                        className="w-full h-full"
                        style={{
                            objectFit: "cover",
                            objectPosition: "center",
                        }}
                    />
                </div>

                {/* RIGHT — form (scrolls naturally, offset half-width on lg+) */}
                <div
                    data-testid="apply-right"
                    className="relative px-6 md:px-12 lg:px-16 xl:px-20 py-14 md:py-20 lg:ml-[50%]"
                    style={{ background: "#1F1F1F", minHeight: "calc(100vh - 80px)" }}
                >
                    <div className="max-w-[640px] mx-auto">
                        <h1
                            className="form-reveal"
                            style={{
                                fontFamily:
                                    '"Figtree", "Inter", system-ui, sans-serif',
                                fontWeight: 800,
                                fontSize: "clamp(2.4rem, 4.6vw, 4rem)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                color: "#F7F4DA",
                            }}
                        >
                            Your VibeCity journey starts here.
                        </h1>

                        <form
                            onSubmit={onSubmit}
                            data-testid="application-form"
                            noValidate
                            className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-7"
                        >
                            {/* Name + Email */}
                            <Field
                                label="Name"
                                required
                                testId="form-full-name"
                                placeholder="Enter Your Name"
                                value={form.full_name}
                                onChange={(v) => setField("full_name", v)}
                            />
                            <Field
                                label="Email"
                                required
                                type="email"
                                testId="form-email"
                                placeholder="you@domain.com"
                                value={form.email}
                                onChange={(v) => setField("email", v)}
                            />

                            {/* LinkedIn + X */}
                            <Field
                                label="LinkedIn"
                                required
                                testId="form-linkedin"
                                placeholder="https://linkedin.com/in/…"
                                value={form.linkedin}
                                onChange={(v) => setField("linkedin", v)}
                            />
                            <Field
                                label="X (Twitter)"
                                testId="form-twitter"
                                placeholder="X (Twitter) URL"
                                value={form.twitter}
                                onChange={(v) => setField("twitter", v)}
                            />

                            {/* Where they're based - freeform, replaces both
                                the anchor-city dropdown and the old "where do
                                you live" field. Sent as `city` to the
                                backend. */}
                            <div className="md:col-span-2">
                                <Field
                                    label="Where are you based? (this is where your events would be hosted)"
                                    required
                                    testId="form-city"
                                    placeholder="City, Country"
                                    value={form.city}
                                    onChange={(v) => setField("city", v)}
                                />
                            </div>

                            {/* Project URL — single column, 2-wide */}
                            <div className="md:col-span-2">
                                <Field
                                    label="Past Projects / Github Repo / Portfolio"
                                    required
                                    testId="form-project-url"
                                    placeholder="Project URLs"
                                    value={form.project_url}
                                    onChange={(v) =>
                                        setField("project_url", v)
                                    }
                                />
                            </div>

                            {/* Organised toggle (full width) */}
                            <div className="md:col-span-2">
                                <div
                                    className="flex items-center justify-between gap-6 rounded-xl px-4 py-4"
                                    style={{
                                        background: "#161616",
                                        border: "1px solid #2E2E2E",
                                    }}
                                >
                                    <div>
                                        <FieldLabel>
                                            I've Organised Events Or Communities
                                            Before
                                        </FieldLabel>
                                        <p
                                            style={{
                                                fontFamily:
                                                    '"Inter", "Figtree", system-ui, sans-serif',
                                                fontSize: 13,
                                                color: "#7A7A72",
                                                marginTop: 4,
                                            }}
                                        >
                                            Toggle on to add detail.
                                        </p>
                                    </div>
                                    <div
                                        data-testid="form-organised-toggle"
                                        role="switch"
                                        aria-checked={form.organised_before}
                                        data-on={form.organised_before}
                                        onClick={() =>
                                            setField(
                                                "organised_before",
                                                !form.organised_before,
                                            )
                                        }
                                        className="vc-toggle"
                                    />
                                </div>
                                {form.organised_before && (
                                    <div className="mt-4 form-reveal">
                                        <Field
                                            label="Tell Us Briefly What You Ran"
                                            type="textarea"
                                            testId="form-organised-details"
                                            placeholder="What you built, who attended, the cadence."
                                            value={form.organised_details}
                                            onChange={(v) =>
                                                setField(
                                                    "organised_details",
                                                    v,
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Who would you bring */}
                            <div className="md:col-span-2">
                                <Field
                                    label="Who Would You Bring Into The Room?"
                                    required
                                    type="textarea"
                                    testId="form-who-youd-bring"
                                    placeholder="The kind of builders, founders and operators you'd curate."
                                    value={form.who_youd_bring}
                                    onChange={(v) =>
                                        setField("who_youd_bring", v)
                                    }
                                />
                            </div>

                            {/* Why you, why this city */}
                            <div className="md:col-span-2">
                                <Field
                                    label="Why You, Why This City, Why Now?"
                                    required
                                    type="textarea"
                                    testId="form-why-you"
                                    placeholder="Your motivation, in your own words."
                                    value={form.why_you}
                                    onChange={(v) => setField("why_you", v)}
                                />
                            </div>

                            {/* Extra */}
                            <div className="md:col-span-2">
                                <Field
                                    label="Anything Else We Should Know?"
                                    type="textarea"
                                    testId="form-extra"
                                    placeholder="Type Here..."
                                    value={form.extra}
                                    onChange={(v) => setField("extra", v)}
                                />
                                <p
                                    style={{
                                        fontFamily:
                                            '"Inter", "Figtree", system-ui, sans-serif',
                                        fontStyle: "italic",
                                        fontSize: 13,
                                        color: "#9A9A92",
                                        marginTop: 8,
                                    }}
                                >
                                    Not Mandatory, But Highly Recommended.
                                </p>
                            </div>

                            {/* Submit */}
                            <div className="md:col-span-2 mt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="form-submit"
                                    className="w-full hover:brightness-105 transition-[filter] disabled:opacity-60"
                                    style={{
                                        padding: "16px 24px",
                                        background:
                                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                                        boxShadow:
                                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                                        borderRadius: 12,
                                        fontFamily:
                                            '"Figtree", "Inter", system-ui, sans-serif',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        letterSpacing: "0.03em",
                                        color: "#0E100F",
                                        border: 0,
                                        cursor: loading
                                            ? "wait"
                                            : "pointer",
                                    }}
                                >
                                    {loading ? "Submitting…" : "Submit"}
                                </button>
                                <p
                                    style={{
                                        fontFamily:
                                            '"Inter", "Figtree", system-ui, sans-serif',
                                        fontSize: 12,
                                        color: "#6F6F66",
                                        marginTop: 14,
                                        textAlign: "center",
                                    }}
                                >
                                    By submitting you agree to be contacted by
                                    the Emergent team.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FieldLabel({ children, required }) {
    return (
        <label
            style={{
                display: "block",
                fontFamily: '"Inter", "Figtree", system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                color: "#C9C9BB",
                marginBottom: 8,
                lineHeight: 1.3,
            }}
        >
            {children}
            {required && (
                <span style={{ color: "#FF4D4F", marginLeft: 4 }}>*</span>
            )}
        </label>
    );
}

function Field({
    label,
    required,
    type = "text",
    testId,
    placeholder,
    value,
    onChange,
}) {
    const isArea = type === "textarea";
    const common = {
        className: "apply-input",
        "data-testid": testId,
        placeholder,
        value: value || "",
        onChange: (e) => onChange(e.target.value),
    };
    return (
        <div>
            <FieldLabel required={required}>{label}</FieldLabel>
            {isArea ? (
                <textarea {...common} rows={4} />
            ) : (
                <input {...common} type={type} />
            )}
        </div>
    );
}

